/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-shadow */
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { Box, TextField, useTheme } from '@mui/material';
import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import weekday from 'dayjs/plugin/weekday';
import React, { useContext, useState } from 'react';
import ETOIconButton from '../Button/ETOIconButton';
import CalendarDayOfWeek from './CalendarDayOfWeek';
import { DateTimeCommonProps, TDatePickersValue } from './DateTimeCommonProps';
import { useCommonClasses } from './DateTimeCommonStyles';
import { SettingsContext } from '../../contexts';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);

/* eslint-disable */
export enum DaysOfTheWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}
/* eslint-enable */
export interface SelectedWeek {
  start: Dayjs;
  end: Dayjs;
  weekStart: DaysOfTheWeek;
  dayInWeek: Dayjs;
}

export interface WeeklyDateSelectorProps
  extends Omit<DateTimeCommonProps, 'onAccept'> {
  datePickerProps?: Omit<
    DatePickerProps<Dayjs, Dayjs>,
    'onChange' | 'value' | 'variant' | 'InputProps' | 'renderInput' | 'disabled'
  >;
  weekStart?: DaysOfTheWeek;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (d: SelectedWeek) => void;
  value: TDatePickersValue;
}

const calculateCustomWeek = (
  selectedDate: TDatePickersValue, // was dayjs
  weekStart: DaysOfTheWeek,
): SelectedWeek => {
  const currentDate = dayjs(selectedDate);

  const chosenStartDate = currentDate.startOf('w').weekday(weekStart);

  return currentDate.isBefore(chosenStartDate) && currentDate
    ? {
      start: chosenStartDate.subtract(7, 'd'),
      end: chosenStartDate,
      weekStart,
      dayInWeek: currentDate,
    }
    : {
      end: chosenStartDate.add(7, 'd'),
      start: chosenStartDate,
      weekStart,
      dayInWeek: currentDate,
    };
};

const formatWeekSelectLabel = (
  theSelectedDate: Dayjs,
  selectedWeek: SelectedWeek,
  format?: string,
) => (theSelectedDate
  ? `${dayjs(selectedWeek.start).format(format || 'MMM D/YY')} - ${dayjs(
    selectedWeek.end,
  )
    .subtract(1, 'd')
    .format(format || 'MMM D/YY')}`
  : 'Invalid Label');

const WeeklyDateSelector = (props: WeeklyDateSelectorProps) => {
  const {
    datePickerProps,
    disabled,
    iconButtonProps,
    InputProps,
    inputFormat,
    onChange,
    weekStart,
    customSx,
    value,
  } = props;

  const theme = useTheme();
  const commonStyles = useCommonClasses(theme);

  const { settings } = useContext(SettingsContext);

  const format = inputFormat ?? settings.dateFormat;
  const dayJsValue = dayjs(value);

  const selectedWeek = calculateCustomWeek(
    dayJsValue,
    weekStart ?? settings.startDayOfWeek,
  );
  const [isOpen, setIsOpen] = useState(false);

  const _toggle = () => setIsOpen((open) => !open);

  const _handleWeekChange = (date: Dayjs) => {
    const newSelection = calculateCustomWeek(
      date,
      weekStart ?? DaysOfTheWeek.Sunday,
    );

    onChange({
      start: newSelection.start,
      end: newSelection.end.subtract(1, 'd'),
      weekStart: selectedWeek.weekStart,
      dayInWeek: date,
    });
  };
  const _handleOnAccept = () => {
    setIsOpen(false);
  };

  const renderWeekPickerDay = (
    date: Dayjs,
    selectedDates: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>,
  ) => {
    if (!value) {
      return <PickersDay {...pickersDayProps} />;
    }

    const { start, end } = selectedWeek;

    const dayIsBetween = date.isBetween(dayjs(start).subtract(1, 'd'), end);
    const isFirstDay = date.isSame(start, 'd');
    const isLastDay = date.isSame(end.subtract(1, 'd'), 'd');
    return (
      <CalendarDayOfWeek
        {...pickersDayProps}
        date={date}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        outsideCurrentMonth={pickersDayProps.outsideCurrentMonth}
        onChange={(e) => _handleWeekChange(e)}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={[
          commonStyles.wrapper,
          ...(Array.isArray(customSx) ? customSx : [customSx]),
        ]}
      >
        <DatePicker
          {...datePickerProps}
          toolbarFormat={formatWeekSelectLabel(
          value as Dayjs,
          selectedWeek,
          format,
          )}
          disabled={disabled}
          inputFormat={formatWeekSelectLabel(
          value as Dayjs,
          selectedWeek,
          format,
          )}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          onChange={(d) => _handleWeekChange(d!)}
          onClose={_handleOnAccept}
          onOpen={() => setIsOpen(true)}
          open={isOpen}
          renderDay={renderWeekPickerDay}
          value={dayjs(value)}
          renderInput={(params) => (
            <TextField
              color="primary"
              variant="standard"
              disabled={disabled}
              {...params}
              sx={[commonStyles.textField]}
              InputProps={
              {
                ...InputProps,
                disableUnderline: true,
                sx: commonStyles.textField,
              } as typeof InputProps
            }
              onClick={() => { if (!disabled) setIsOpen(true); }}
              value={formatWeekSelectLabel(value as Dayjs, selectedWeek, format)}
              onKeyDown={(e) => e.preventDefault()}
            />
          )}
        />

        <ETOIconButton
          {...iconButtonProps}
          color={iconButtonProps?.color || 'primary'}
          disabled={disabled}
          onClick={() => _toggle()}
          size={iconButtonProps?.size || 'small'}
        >
          {isOpen ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        </ETOIconButton>
      </Box>
    </LocalizationProvider>
  );
};

export default WeeklyDateSelector;
